import React from 'react';

import { Collapsible, CollapsibleHeaderContent, ItemName } from '@dndbeyond/character-components/es';
import {
    CampaignUtils,
    Container,
    ContainerUtils,
    CharacterTheme,
    HelperUtils,
    Item,
    ItemUtils,
    RuleData,
    MenuOption,
    PartyInfo,
    Constants,
} from '@dndbeyond/character-rules-engine/es';

import ItemDetail from '../../ItemDetail';
import { ThemeButton, ThemeButtonWithMenu } from '../../common/Button';

interface Props {
    item: Item;
    enableAdd: boolean;
    onItemAdd?: (item: Item, amount: number, containerDefinitionKey: string) => void;
    minAddAmount: number;
    maxAddAmount: number;
    ruleData: RuleData;
    proficiencyBonus: number;
    containers: Array<Container>;
    theme: CharacterTheme;
    partyInfo: PartyInfo | null;
}
interface State {
    amount: number | null;
}
export default class EquipmentShopItem extends React.PureComponent<Props, State> {
    static defaultProps = {
        enableAdd: true,
        minAddAmount: 1,
        maxAddAmount: 10,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            amount: 1,
        };
    }

    handleAmountChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            amount: HelperUtils.parseInputInt(evt.target.value),
        });
    };

    handleAmountBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
        const { minAddAmount, maxAddAmount } = this.props;

        const parsedValue = HelperUtils.parseInputInt(evt.target.value);
        const clampedValue = HelperUtils.clampInt(parsedValue === null ? 0 : parsedValue, minAddAmount, maxAddAmount);

        this.setState({
            amount: clampedValue,
        });
    };

    handleDecrementCountClick = (): void => {
        const { minAddAmount } = this.props;

        this.setState((prevState, props) => ({
            amount: Math.max((prevState.amount === null ? 0 : prevState.amount) - 1, minAddAmount),
        }));
    };

    handleIncrementCountClick = (): void => {
        this.setState((prevState, props) => ({
            amount: (prevState.amount === null ? 0 : prevState.amount) + 1,
        }));
    };

    handleAmountKeyUp = (evt: React.KeyboardEvent<HTMLInputElement>): void => {
        const { minAddAmount, maxAddAmount } = this.props;

        const parsedValue = HelperUtils.parseInputInt((evt.target as HTMLInputElement).value);
        const clampedValue = HelperUtils.clampInt(parsedValue === null ? 0 : parsedValue, minAddAmount, maxAddAmount);

        this.setState({
            amount: clampedValue,
        });
    };

    handleAdd = (containerDefinitionKey?: string): void => {
        const { amount } = this.state;
        const { item, onItemAdd, minAddAmount, containers } = this.props;

        if (onItemAdd) {
            let itemDestinationContainerDefinitionKey = containerDefinitionKey;
            // Default to the character container if none is passed
            if (!itemDestinationContainerDefinitionKey) {
                itemDestinationContainerDefinitionKey = ContainerUtils.getDefinitionKey(
                    containers.find(ContainerUtils.isCharacterContainer) ?? containers[0],
                );
            }

            onItemAdd(item, amount === null ? minAddAmount : amount, itemDestinationContainerDefinitionKey ?? '');
        }
    };

    renderHeader = (item: Item, metaItems: Array<string>): React.ReactNode => {
        const { theme, containers, partyInfo } = this.props;

        return (
            <CollapsibleHeaderContent
                heading={<ItemName item={item} theme={theme} />}
                metaItems={metaItems}
                callout={
                    <ThemeButtonWithMenu
                        groupedOptions={ContainerUtils.getGroupedOptions(
                            null,
                            containers,
                            'Add To:',
                            partyInfo
                                ? CampaignUtils.getSharingState(partyInfo)
                                : Constants.PartyInventorySharingStateEnum.OFF,
                        )}
                        onSelect={this.handleAdd}
                    >
                        Add
                    </ThemeButtonWithMenu>
                }
            />
        );
    };

    render() {
        const { amount } = this.state;
        const { item, minAddAmount, maxAddAmount, ruleData, proficiencyBonus, theme, containers, partyInfo } =
            this.props;

        let metaItems: Array<string> = [];

        const type = ItemUtils.getType(item);
        if (type) {
            metaItems.push(type);
        }

        if (ItemUtils.isArmorContract(item)) {
            const baseArmorName = ItemUtils.getBaseArmorName(item);
            if (baseArmorName) {
                metaItems.push(baseArmorName);
            }
        } else if (ItemUtils.isGearContract(item)) {
            const subType = ItemUtils.getSubType(item);
            if (subType) {
                metaItems.push(subType);
            }
        }

        const totalAmount: number = (amount === null ? 1 : amount) * ItemUtils.getBundleSize(item);

        return (
            <Collapsible
                layoutType={'minimal'}
                header={this.renderHeader(item, metaItems)}
                className="ct-equipment-shop__item"
            >
                <ItemDetail
                    theme={theme}
                    item={item}
                    ruleData={ruleData}
                    showCustomize={false}
                    showActions={false}
                    showImage={false}
                    proficiencyBonus={proficiencyBonus}
                />
                <div className="ct-equipment-shop__item-actions">
                    <div className="ct-equipment-shop__item-action">
                        <div className="ct-equipment-shop__item-amount">
                            <div className="ct-equipment-shop__item-amount-label">Amount to add</div>
                            <div className="ct-equipment-shop__item-amount-controls">
                                <div className="ct-equipment-shop__item-amount-decrease">
                                    <ThemeButton
                                        className="button-action-decrease button-action-decrease--small"
                                        onClick={this.handleDecrementCountClick}
                                        disabled={amount !== null && amount <= minAddAmount}
                                        size="small"
                                    />
                                </div>
                                <div className="ct-equipment-shop__item-amount-value">
                                    <input
                                        type="number"
                                        value={amount === null ? '' : amount}
                                        className="character-input ct-equipment-shop__item-amount-input"
                                        onChange={this.handleAmountChange}
                                        onBlur={this.handleAmountBlur}
                                        onKeyUp={this.handleAmountKeyUp}
                                        min={minAddAmount}
                                        max={maxAddAmount}
                                    />
                                </div>
                                <div className="ct-equipment-shop__item-amount-increase">
                                    <ThemeButton
                                        className="button-action-increase button-action-increase--small"
                                        onClick={this.handleIncrementCountClick}
                                        disabled={amount !== null && amount >= maxAddAmount}
                                        size="small"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="ct-equipment-shop__item-action">
                        <ThemeButtonWithMenu
                            onSelect={(definitionKey) => this.handleAdd(definitionKey)}
                            containerEl={document.querySelector('.ct-sidebar__portal') as HTMLElement}
                            groupedOptions={ContainerUtils.getGroupedOptions(
                                null,
                                containers,
                                'Add To:',
                                partyInfo
                                    ? CampaignUtils.getSharingState(partyInfo)
                                    : Constants.PartyInventorySharingStateEnum.OFF,
                            )}
                        >
                            Add {totalAmount === 1 ? ' Item' : ` ${totalAmount} Items`}
                        </ThemeButtonWithMenu>
                    </div>
                </div>
            </Collapsible>
        );
    }
}
